import { useEffect, useMemo, useState } from "react";
import { oauth } from "../../../utils";

interface Customer {
  Id: string;
  DisplayName: string;
}
interface UseQuickbooksCustomersHookProps {
  qboCustomerRef: number;
}
export function useQuickbooksCustomers(props: UseQuickbooksCustomersHookProps) {
  const { qboCustomerRef } = props;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentCustomer, setCurrentCustomer] = useState<Customer | null>(null);
  const [allCustomers, setAllCustomers] = useState([]);

  const operations = useMemo(
    () => ({
      getAllCustomers: async () => {
        try {
          setLoading(true);
          const response = await oauth("POST", "quickbooks", {
            action: "get_entity",
            entity_name: "Customer",
          });

          if (!response.json) {
            setError("Unable to get customers");
            return;
          }

          const transformed = Object.values(response.json.customers).map(
            (customer: Customer) => ({
              id: customer.Id,
              displayName: customer.DisplayName,
            })
          );

          setAllCustomers(transformed);
        } catch (error) {
          setError(error.toString());
        } finally {
          setLoading(false);
        }
      },
      getCustomerById: async () => {
        try {
          setLoading(true);

          const response = await oauth("POST", "quickbooks", {
            action: "get_entity",
            entity_name: "Customer",
            qbo_customer_ref: qboCustomerRef,
          });

          if (!response.json) {
            setError("Unable to get customer");
            return;
          }

          setCurrentCustomer(response.json.customer);
        } catch (error) {
          setError(error.toString());
        } finally {
          setLoading(false);
        }
      },
    }),
    [qboCustomerRef]
  );

  useEffect(() => {
    if (allCustomers.length === 0) {
      operations.getCustomerById();
      operations.getAllCustomers();
    }
  }, [allCustomers, operations]);

  return {
    loading,
    error,
    allCustomers,
    currentCustomer,
    operations,
  };
}
